/**
 * Comp class evals in the browser: the family-readable dates (a live
 * listener on compEvalDates) and the staff lane (list, ask, set, cancel).
 * The pure rules and copy are in ./compEvals.ts; the server is
 * netlify/functions/compEvals.mjs.
 */
import { useCallback, useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { functionHeaders } from './functionSecurity';
import {
  COMP_EVALS_ENDPOINT,
  normalizeCompEvalDate,
  normalizeCompEvalRecord,
  normalizeCompEvalSlot,
  type CompEvalDate,
  type CompEvalRecord,
  type CompEvalSlot,
  type CompEvalSlotTime,
} from './compEvals';

/**
 * Every confirmed eval date, keyed by offeringId. One small collection (one
 * doc per comp class with a date), so one listener for the whole app page,
 * handed down to every card rather than one per card.
 *
 * Honest state: a failed listener comes back as `error`, and callers draw
 * nothing rather than "no eval scheduled".
 */
export function useCompEvalDates(enabled = true): {
  dates: Map<string, CompEvalDate>;
  loading: boolean;
  error: string | null;
} {
  const [dates, setDates] = useState<Map<string, CompEvalDate>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!db || !enabled) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      collection(db, 'compEvalDates'),
      (snap) => {
        const next = new Map<string, CompEvalDate>();
        snap.forEach((docSnap) => {
          const entry = normalizeCompEvalDate(docSnap.id, docSnap.data());
          if (entry) next.set(docSnap.id, entry);
        });
        setDates(next);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }, [enabled]);
  return { dates, loading, error };
}

const ERROR_COPY: Record<string, string> = {
  not_allowed: 'Only this class\'s teachers, a Director or a Manager can do that.',
  sign_in_required: 'Your sign-in ran out. Sign in again, then try once more.',
  class_not_found: 'This class was removed.',
  with_not_scheduler: 'Pick a Director or a Manager to schedule it with.',
  already_scheduled: 'This eval already has a date. A Director or Manager can move it.',
  not_comp_class: 'Evals are for comp classes only.',
  bad_date: 'Pick a real date.',
  bad_time: 'Check the times. The end has to be after the start.',
  note_too_long: 'That note is too long. Trim it, then try again.',
  too_many_requests: 'Too many tries at once. Wait a minute, then try again.',
  slot_taken: 'Another class just picked that time. Pick another one.',
  slot_gone: 'That time was taken down. Pick another one.',
  past_date: 'That day has already passed.',
  too_many_slots: 'That is more than 200 spots at once. Add them in two goes.',
  sheet_too_large: 'That sheet is too big. Keep it under half a megabyte, or paste the times instead.',
  unreadable_sheet: 'That file could not be read as an Excel sheet. Save it as .xlsx, or paste the times instead.',
  request_too_large: 'That is too much at once. Add the times in two goes.',
  offline: 'You look offline. Check your connection, then try again.',
};

export class CompEvalsRequestError extends Error {
  code: string;
  constructor(code: string) {
    super(ERROR_COPY[code] ?? 'That did not save. Please try again.');
    this.code = code;
  }
}

async function post<T>(user: User | null, body: Record<string, unknown>): Promise<T> {
  if (!user) throw new CompEvalsRequestError('sign_in_required');
  let headers: Record<string, string>;
  try {
    headers = await functionHeaders(user);
  } catch {
    throw new CompEvalsRequestError('offline');
  }
  let response: Response;
  try {
    response = await fetch(COMP_EVALS_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers,
      body: JSON.stringify(body),
    });
  } catch {
    throw new CompEvalsRequestError('offline');
  }
  const payload = (await response.json().catch(() => null)) as (T & { ok?: boolean; error?: string }) | null;
  if (!response.ok || payload?.ok !== true) throw new CompEvalsRequestError(payload?.error ?? 'server_error');
  return payload;
}

export interface CompEvalWriteResult {
  offeringId: string;
  status: 'requested' | 'scheduled' | 'none';
  withName?: string;
  moved?: boolean;
  previousDate?: string;
  /** The old date's class board post, for the card to take down. */
  previousPostId?: string;
  taskId?: string;
  taskAssigneeUid?: string;
  /** The lane ticked the ask's to-do in the same write. */
  taskClosed?: boolean;
}

export function requestCompEval(
  user: User | null,
  input: { offeringId: string; withUid: string; preferredDate: string; preferredTime: string; note: string; slotId?: string },
): Promise<CompEvalWriteResult> {
  return post(user, { action: 'request', ...input });
}

/** Add the signed-in Director's or Manager's own 10-minute spots. */
export function addCompEvalSlots(
  user: User | null,
  slots: readonly CompEvalSlotTime[],
): Promise<{ added: number; alreadyThere: number }> {
  return post(user, {
    action: 'addSlots',
    slots: slots.map((s) => ({ date: s.date, startTime: s.startTime, endTime: s.endTime })),
  });
}

export function removeCompEvalSlot(user: User | null, slotId: string): Promise<{ removed: boolean }> {
  return post(user, { action: 'removeSlot', slotId });
}

/** Take down several open spots at once (a whole day). Held ones are left and counted. */
export function removeCompEvalSlots(user: User | null, slotIds: readonly string[]): Promise<{ removed: number; held: number }> {
  return post(user, { action: 'removeSlots', slotIds: [...slotIds] });
}

/** Read an uploaded .xlsx into rows of cell text. Nothing is saved. */
export async function readCompEvalSheet(user: User | null, file: File): Promise<string[][]> {
  const buffer = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < buffer.length; i += 0x8000) {
    binary += String.fromCharCode(...buffer.subarray(i, i + 0x8000));
  }
  const payload = await post<{ rows?: unknown }>(user, { action: 'readSheet', contentBase64: btoa(binary) });
  return Array.isArray(payload.rows)
    ? payload.rows.map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : []))
    : [];
}

export function linkCompEvalTask(user: User | null, offeringId: string, taskId: string): Promise<CompEvalWriteResult> {
  return post(user, { action: 'linkTask', offeringId, taskId });
}

export function linkCompEvalPost(
  user: User | null,
  offeringId: string,
  postId: string,
  date: string,
): Promise<CompEvalWriteResult> {
  return post(user, { action: 'linkPost', offeringId, postId, date });
}

export function scheduleCompEval(
  user: User | null,
  input: { offeringId: string; date: string; startTime: string; endTime: string; withUid: string },
): Promise<CompEvalWriteResult> {
  return post(user, { action: 'schedule', ...input });
}

export function cancelCompEval(user: User | null, offeringId: string): Promise<CompEvalWriteResult> {
  return post(user, { action: 'cancel', offeringId });
}

/**
 * The staff records (requests and dates), through the lane. Not a listener:
 * the lane is the only reader. `reload` after a write; the caller's own write
 * result is applied at once so the card never shows the old state.
 */
export function useCompEvalRecords(user: User | null, enabled: boolean): {
  records: Map<string, CompEvalRecord>;
  /** Every upcoming eval spot, earliest first (open, asked and booked). */
  slots: CompEvalSlot[];
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [records, setRecords] = useState<Map<string, CompEvalRecord>>(new Map());
  const [slots, setSlots] = useState<CompEvalSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!enabled || !user) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    post<{ evals?: unknown[]; slots?: unknown[] }>(user, { action: 'list' })
      .then((payload) => {
        if (cancelled) return;
        const next = new Map<string, CompEvalRecord>();
        for (const raw of Array.isArray(payload.evals) ? payload.evals : []) {
          const id = raw && typeof raw === 'object' ? (raw as { offeringId?: unknown }).offeringId : null;
          if (typeof id !== 'string' || !id) continue;
          const entry = normalizeCompEvalRecord(id, raw);
          if (entry) next.set(id, entry);
        }
        setRecords(next);
        const nextSlots: CompEvalSlot[] = [];
        for (const raw of Array.isArray(payload.slots) ? payload.slots : []) {
          const id = raw && typeof raw === 'object' ? (raw as { slotId?: unknown }).slotId : null;
          if (typeof id !== 'string' || !id) continue;
          const slot = normalizeCompEvalSlot(id, raw);
          if (slot) nextSlots.push(slot);
        }
        setSlots(nextSlots);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Eval dates could not load.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, enabled, nonce]);
  return { records, slots, loading, error, reload };
}
